import { Card, Row, Col, Progress, Typography, Flex, Tag } from "antd";
import { ClockCircleOutlined, MessageOutlined } from "@ant-design/icons";
import CustomModal from "../../components/CustomModal/CustomModal";
import CustomButton from "../../components/CustomButton/Button";
import BackButton from "../../components/CustomButton/BackButton";
import ReasonForm from "../../components/CustomForms/ReasonForm";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getIdQuestionnaireThread,
  approveRejectAnswer,
  updateQuestionnaireThreadStatus
} from "../../redux/slices/QuestionBankSlice";
import { DateFormatter } from "../../helpers/helpers";
import { isDeprecatedJvTenureQuestion } from "../../helpers/jvQuestionnaire";
import {
  shouldShowManualKycFields,
  formatManualKycAnswerForDisplay,
  isManualEntryAnswer,
  parseManualKycAnswerText,
} from "../../helpers/manualKycQuestionnaire";
import {
  isQuestionnaireQuestionOptional,
  isSkippedOptionalAnswer,
} from "../../helpers/questionnaireOptional";
import "./EnqMngt.scss";

const ReviewAllThreads = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { orgId } = useParams();

    const [fileUrl, setFileUrl] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [reviewModal, setReviewModal] = useState({
        type: null,
        answerId: null,
    });


    const { questionnaireThreads, orgData, isCreditAssessment } = location.state || {};
    const { idQuestionnaireThread } = useSelector((state) => state.questionBank);

    const [activeThread, setActiveThread] = useState(
        questionnaireThreads?.[0] || null
    );

    // ✅ Restricted question IDs
    const restrictedQuestionIds = [
        "QBM1000043",
        "QBM1000015",
        "QBM1000014",
        "QBM1000002"
    ];

    // ✅ Check dealId exists
    const hasDealId = !!idQuestionnaireThread?.dealId;

    useEffect(() => {
        if (activeThread?.threadId) {
            dispatch(getIdQuestionnaireThread(activeThread.threadId));
        }
    }, [dispatch, activeThread]);

    // Refetch when admin returns to the tab so business resubmits are not stale
    useEffect(() => {
        const refetchActiveThread = () => {
            if (
                document.visibilityState === "visible" &&
                activeThread?.threadId
            ) {
                dispatch(getIdQuestionnaireThread(activeThread.threadId));
            }
        };

        document.addEventListener("visibilitychange", refetchActiveThread);
        window.addEventListener("focus", refetchActiveThread);

        return () => {
            document.removeEventListener("visibilitychange", refetchActiveThread);
            window.removeEventListener("focus", refetchActiveThread);
        };
    }, [dispatch, activeThread?.threadId]);

    const getAnswerTimestamp = (answer) =>
        new Date(answer?.submittedAt || answer?.createdAt || 0).getTime();

    const getAnswerStatus = (answer) => answer?.status || answer?.answerStatus;

    const getAnswersByStatus = (answers, status) =>
        answers
            .filter((a) => getAnswerStatus(a) === status)
            .sort((a, b) => getAnswerTimestamp(a) - getAnswerTimestamp(b));

    const getDisplayAnswer = (answers, questionStatus) => {
        if (!answers || answers.length === 0) return null;

        const pendingAnswers = getAnswersByStatus(answers, "PENDING_REVIEW");
        const approvedAnswers = getAnswersByStatus(answers, "APPROVED");
        const rejectedAnswers = getAnswersByStatus(answers, "REJECTED");

        const latestPending = pendingAnswers[pendingAnswers.length - 1];
        const latestApproved = approvedAnswers[approvedAnswers.length - 1];
        const latestRejected = rejectedAnswers[rejectedAnswers.length - 1];

        // Prefer PENDING_REVIEW only when it is the newest answer (real resubmit)
        if (latestPending) {
            const pendingTs = getAnswerTimestamp(latestPending);
            const approvedTs = latestApproved ? getAnswerTimestamp(latestApproved) : 0;
            const rejectedTs = latestRejected ? getAnswerTimestamp(latestRejected) : 0;
            if (pendingTs >= approvedTs && pendingTs >= rejectedTs) {
                return latestPending;
            }
        }

        if (questionStatus === "APPROVED" && latestApproved) {
            return latestApproved;
        }

        if (questionStatus === "REJECTED" && latestRejected) {
            return latestRejected;
        }

        if (latestApproved) {
            return latestApproved;
        }

        if (latestRejected) {
            return latestRejected;
        }

        const sortedAnswers = [...answers].sort(
            (a, b) => getAnswerTimestamp(a) - getAnswerTimestamp(b),
        );
        return sortedAnswers[sortedAnswers.length - 1];
    };

    const getQuestionReviewState = (questionStatus, answer) => {
        const answerStatus = getAnswerStatus(answer);

        // Pending resubmission must unlock review even if question status is stale APPROVED/REJECTED
        if (answerStatus === "PENDING_REVIEW") {
            return "PENDING_REVIEW";
        }

        if (questionStatus === "APPROVED" || answerStatus === "APPROVED") {
            return "APPROVED";
        }

        if (questionStatus === "REJECTED" || answerStatus === "REJECTED") {
            return "REJECTED";
        }

        return "AWAITING";
    };

    const handleViewQuestion = (question, overrideUrl) => {
        const answer = getDisplayAnswer(question.answers, question.status);
        setFileUrl(overrideUrl || answer?.fileUrl);
        setOpenModal(true);
    };

    const openReviewModal = (type, answerId) => {
        setReviewModal({
            type,
            answerId,
        });
    };

    const closeReviewModal = () => {
        setReviewModal({
            type: null,
            answerId: null,
        });
    };

    const handleReviewSubmit = async (values) => {
        const payload = {
            answerId: reviewModal.answerId,
            reviewStatus: reviewModal.type,
            reviewComment: values.reason,
        };

        const res = await dispatch(approveRejectAnswer(payload));

        if (approveRejectAnswer.fulfilled.match(res)) {
            await dispatch(getIdQuestionnaireThread(activeThread.threadId));
            closeReviewModal();
        }
    };

    const handleMarkThreadCompleted = async () => {
        const res = await dispatch(updateQuestionnaireThreadStatus({ threadId: activeThread.threadId, status: "COMPLETED" }));
        if (res.payload?.statusCode === 200) {
            navigate(`/enquiry-management/send-questionnaire/${orgId}`, { state: { isCreditAssessment, dealId: hasDealId ? idQuestionnaireThread?.dealId : null, fromDeals: hasDealId, dealCategoryId: orgData?.dealCategoryId } });
        }
    };

    const isJvThread = orgData?.dealCategoryId === "DCT1000002"
        || idQuestionnaireThread?.dealCategoryId === "DCT1000002";

    const reviewQuestions = (idQuestionnaireThread?.questions || []).filter((question) => {
        if (!isJvThread) {
            return true;
        }

        return !isDeprecatedJvTenureQuestion(question?.questionDetails?.questionTitle);
    });

    const totalQuestions = reviewQuestions.length;

    const answeredCount =
        reviewQuestions.filter(
            (q) => q.answers && q.answers.length > 0
        ).length || 0;

    const progressPercent =
        totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

    const allApproved =
        reviewQuestions.length > 0 &&
        reviewQuestions.every((q) => {
            if (q.status === "APPROVED") return true;
            const answer = getDisplayAnswer(q.answers, q.status);
            return getAnswerStatus(answer) === "APPROVED";
        });

    return (
        <div className="review-thread-container">
            <BackButton />

            <Typography.Title level={2}>
                Questionnaire Threads & Review
            </Typography.Title>

            <Row gutter={20}>
                {/* LEFT THREAD LIST */}
                <Col span={8}>
                    <Card title="Questionnaire Threads">
                        {questionnaireThreads?.map((thread) => (
                            <Card
                                key={thread.threadId}
                                className={`thread-card ${activeThread?.threadId === thread.threadId ? "active" : ""
                                    }`}
                                onClick={() => setActiveThread(thread)}
                            >
                                <div className="thread-header">
                                    <h4>{thread.threadName}</h4>
                                    {/* <Tag color="orange">{thread.status}</Tag> */}
                                </div>

                                {/* <p className="thread-subtitle">Basic Information</p> */}

                                <p className="thread-date">
                                    <ClockCircleOutlined /> Sent on{" "}
                                    {DateFormatter(thread.sentAt)}
                                </p>
                            </Card>
                        ))}
                    </Card>
                </Col>

                {/* RIGHT REVIEW PANEL */}
                <Col span={16}>
                    <Card
                        title="Questionnaire Review"
                    // extra={<Tag color="default">{activeThread?.status}</Tag>}
                    >
                        <div className="review-header">
                            <span>
                                <ClockCircleOutlined /> Sent on{" "}
                                {DateFormatter(activeThread?.sentAt)} •{" "}
                                {activeThread?.questions?.length} questions
                            </span>

                            <span>
                                {answeredCount}/{totalQuestions}
                            </span>
                        </div>

                        <Progress percent={progressPercent} showInfo={false} />

                        {/* QUESTIONS */}
                        {reviewQuestions.map((q, index) => {
                            const answer = getDisplayAnswer(q.answers, q.status);

                            const answerId = answer?.answerId;
                            const reviewState = getQuestionReviewState(q.status, answer);
                            const isReviewLocked =
                                reviewState === "APPROVED" || reviewState === "REJECTED";

                            return (
                                <Card key={index} className="question-review-card">
                                    <div className="review-question-header">
                                        <h4>
                                            Question {index + 1}
                                            {isQuestionnaireQuestionOptional(q?.questionDetails) && (
                                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                                    Optional
                                                </Tag>
                                            )}
                                        </h4>
                                        <p>{q?.questionDetails?.questionTitle}</p>
                                    </div>

                                    {q.answers.length === 0 ? (
                                        <div className="awaiting-box">
                                            <ClockCircleOutlined />
                                            <span>Awaiting business response</span>
                                        </div>
                                    ) : isSkippedOptionalAnswer(answer?.answerText) ? (
                                        <div className="answer-section">
                                            <h4>Answer:</h4>
                                            <Flex align="center" gap={8}>
                                                <MessageOutlined />
                                                <span>{answer.answerText}</span>
                                            </Flex>
                                        </div>
                                    ) : (
                                        <div className="answer-section">
                                            {shouldShowManualKycFields(
                                                q.questionId,
                                                idQuestionnaireThread?.threadName ||
                                                    activeThread?.threadName,
                                                answer?.answerText,
                                            ) &&
                                                answer?.answerText && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <Flex align="center" gap={8} style={{ marginBottom: 8 }}>
                                                            <h4 style={{ margin: 0 }}>Entered values:</h4>
                                                            {isManualEntryAnswer(answer.answerText) && (
                                                                <Tag color="orange">Manual entry</Tag>
                                                            )}
                                                        </Flex>
                                                        <Flex align="flex-start" gap={8}>
                                                            <MessageOutlined />
                                                            <span style={{ whiteSpace: "pre-line" }}>
                                                                {formatManualKycAnswerForDisplay(
                                                                    answer.answerText,
                                                                ) || answer.answerText}
                                                            </span>
                                                        </Flex>
                                                    </div>
                                                )}
                                            {(() => {
                                                const parsedManual =
                                                    parseManualKycAnswerText(
                                                        answer?.answerText,
                                                    );
                                                const panFileUrl =
                                                    parsedManual?.panFileUrl;
                                                const hasAadhaarDoc =
                                                    Boolean(answer?.fileUrl);
                                                const isDirectorPair =
                                                    q.questionId === "QBM1000043" ||
                                                    Boolean(
                                                        parsedManual?.aadhaar &&
                                                            parsedManual?.pan,
                                                    );

                                                if (
                                                    q?.questionDetails
                                                        ?.questionTypeId ===
                                                        "QTM1000008" ||
                                                    hasAadhaarDoc ||
                                                    panFileUrl
                                                ) {
                                                    return (
                                                        <Flex gap={8} wrap="wrap">
                                                            {hasAadhaarDoc && (
                                                                <CustomButton
                                                                    onClick={() =>
                                                                        handleViewQuestion(
                                                                            q,
                                                                        )
                                                                    }
                                                                >
                                                                    {isDirectorPair
                                                                        ? "View Aadhaar Document"
                                                                        : "View Document"}
                                                                </CustomButton>
                                                            )}
                                                            {panFileUrl && (
                                                                <CustomButton
                                                                    onClick={() =>
                                                                        handleViewQuestion(
                                                                            q,
                                                                            panFileUrl,
                                                                        )
                                                                    }
                                                                >
                                                                    View PAN Document
                                                                </CustomButton>
                                                            )}
                                                            {!hasAadhaarDoc &&
                                                                !panFileUrl &&
                                                                q?.questionDetails
                                                                    ?.questionTypeId ===
                                                                    "QTM1000008" && (
                                                                    <CustomButton
                                                                        onClick={() =>
                                                                            handleViewQuestion(
                                                                                q,
                                                                            )
                                                                        }
                                                                    >
                                                                        View Document
                                                                    </CustomButton>
                                                                )}
                                                        </Flex>
                                                    );
                                                }

                                                if (
                                                    !isManualEntryAnswer(
                                                        answer?.answerText,
                                                    )
                                                ) {
                                                    return (
                                                        <>
                                                            <h4>Answer:</h4>
                                                            <Flex
                                                                align="center"
                                                                gap={8}
                                                            >
                                                                <MessageOutlined />
                                                                <span>
                                                                    {
                                                                        answer?.answerText
                                                                    }
                                                                </span>
                                                            </Flex>
                                                        </>
                                                    );
                                                }

                                                return null;
                                            })()}
                                        </div>
                                    )}

                                    {/* REVIEW BUTTONS — digital HV defaults stay locked on deals;
                                        Switch-to-Manual answers always need Admin approve/reject */}
                                    {q.answers.length > 0 &&
                                        !(
                                            hasDealId &&
                                            restrictedQuestionIds.includes(q.questionId) &&
                                            !isManualEntryAnswer(answer?.answerText)
                                        ) && (
                                            <div className="review-buttons">
                                                <CustomButton
                                                    type={reviewState === "APPROVED" ? "primary" : "default"}
                                                    disabled={isReviewLocked}
                                                    onClick={() => openReviewModal("APPROVED", answerId)}
                                                >
                                                    {reviewState === "APPROVED"
                                                        ? "✔ Approved"
                                                        : "✔ Approve"}
                                                </CustomButton>

                                                <CustomButton
                                                    danger={reviewState !== "REJECTED"}
                                                    type={reviewState === "REJECTED" ? "primary" : "default"}
                                                    disabled={isReviewLocked}
                                                    onClick={() => openReviewModal("REJECTED", answerId)}
                                                >
                                                    {reviewState === "REJECTED"
                                                        ? "✖ Rejected"
                                                        : "✖ Reject"}
                                                </CustomButton>
                                            </div>
                                        )}
                                </Card>
                            );
                        })}

                        <div className="review-footer">
                            <span>Review all answers to complete this thread</span>

                            <CustomButton
                                type="primary"
                                onClick={handleMarkThreadCompleted}
                                disabled={!allApproved || activeThread?.status === "COMPLETED"}
                            >
                                {activeThread?.status === "COMPLETED"
                                    ? "Thread Completed"
                                    : "Mark Thread as Completed"}
                            </CustomButton>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* FILE VIEW MODAL */}
            <CustomModal
                title="View Answer"
                open={openModal}
                width={800}
                closeIcon
                onCancel={() => setOpenModal(false)}
                content={
                    <iframe
                        src={fileUrl}
                        title="Answer File"
                        style={{ width: "100%", height: "400px" }}
                    />
                }
            />

            {/* REVIEW MODAL */}
            {reviewModal.type && (
                <CustomModal
                    title={
                        reviewModal.type === "APPROVED"
                            ? "Approve Answer"
                            : "Reject Answer"
                    }
                    open
                    centered
                    content={
                        <ReasonForm
                            label="Comment"
                            placeholder="Write your comment"
                            btnName="Submit"
                            btnType={
                                reviewModal.type === "REJECTED" ? "danger" : "primary"
                            }
                            onClose={closeReviewModal}
                            handleOnFormSubmit={handleReviewSubmit}
                        />
                    }
                />
            )}
        </div>
    );
};

export default ReviewAllThreads;